import React, { useState, useMemo, useRef } from 'react';
import {
  textToAoa, readWorkbook, sheetToAoa, pickBestSheet, aoaToTable, hintsFor, makeGuesser, buildRecords,
} from './tabularImportUtils';

/**
 * TabularImport — schema-driven paste / CSV / .xlsx importer with header-row
 * detection, column mapping and a live preview. Generalises the BOM importer so
 * any register can be bulk-imported.
 *
 * props:
 *   schema   { fields:[{key,label,guess:[RegExp],type,default,identifier}], keepExtras }
 *   onRecords(records[])  — called whenever the mapped, previewable rows change
 *   accent   highlight colour (defaults to a neutral green)
 *   sampleHeaders  optional placeholder header list for the paste box
 */
export default function TabularImport({ schema, onRecords, accent = '#46BB89', sampleHeaders }) {
  const fields = useMemo(() => schema.fields || [], [schema]);
  const keepExtras = schema.keepExtras !== false;
  const hints = useMemo(() => hintsFor(schema), [schema]);
  const guesser = useMemo(() => makeGuesser(schema), [schema]);
  const fieldOptions = useMemo(() => [
    ...fields,
    ...(keepExtras ? [{ key: '__extra', label: 'Keep as extra' }] : []),
    { key: '__ignore', label: 'Ignore' },
  ], [fields, keepExtras]);
  const fieldKeys = useMemo(() => fields.map(f => f.key), [fields]);

  const [raw, setRaw] = useState('');
  const [aoa, setAoa] = useState([]);
  const [headerRow, setHeaderRow] = useState(0);
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [wb, setWb] = useState(null);          // parsed workbook (for multi-sheet)
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState('');
  const fileRef = useRef(null);

  const emit = (mapObj, p) => { if (onRecords) onRecords(buildRecords(mapObj, p, schema)); };

  const applyTable = (table) => {
    const map = {};
    table.headers.forEach((h, i) => { map[i] = guesser(h) || (keepExtras ? '__extra' : '__ignore'); });
    const p = { headers: table.headers, rows: table.rows };
    setParsed(p); setMapping(map);
    emit(map, p);
  };

  const ingestAoa = (rows, name) => {
    setAoa(rows);
    const table = aoaToTable(rows, null, hints);
    setHeaderRow(table.headerIndex);
    setFileName(name || '');
    applyTable(table);
  };

  const changeHeaderRow = (idx) => { setHeaderRow(idx); applyTable(aoaToTable(aoa, idx, hints)); };

  // Switch to another sheet in the loaded workbook — re-detects its header row.
  const changeSheet = (name) => { setSheet(name); ingestAoa(sheetToAoa(wb, name), fileName); };

  const clearWorkbook = () => { setWb(null); setSheetNames([]); setSheet(''); };

  const onPaste = e => { clearWorkbook(); setRaw(e.target.value); ingestAoa(textToAoa(e.target.value), ''); };

  const onFile = file => {
    if (!file) return;
    const name = file.name || '';
    const reader = new FileReader();
    if (/\.(xlsx|xlsm|xls)$/i.test(name)) {
      reader.onload = ev => {
        try {
          const { wb: workbook, sheetNames: names } = readWorkbook(ev.target.result);
          const chosen = pickBestSheet(workbook, names, hints);
          setWb(workbook); setSheetNames(names); setSheet(chosen);
          ingestAoa(sheetToAoa(workbook, chosen), name); setRaw('');
        } catch { clearWorkbook(); setFileName(`Could not read ${name}`); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = ev => { const text = String(ev.target.result || ''); clearWorkbook(); setRaw(text); ingestAoa(textToAoa(text), name); };
      reader.readAsText(file);
    }
  };

  const setColMap = (col, key) => {
    const next = { ...mapping };
    if (fieldKeys.includes(key)) {
      Object.keys(next).forEach(c => { if (next[c] === key && Number(c) !== Number(col)) next[c] = keepExtras ? '__extra' : '__ignore'; });
    }
    next[col] = key;
    setMapping(next);
    emit(next, parsed);
  };

  const previewRecords = useMemo(() => buildRecords(mapping, parsed, schema), [mapping, parsed, schema]);

  const missingIds = fields.filter(f => f.identifier).filter(f => !Object.values(mapping).includes(f.key));

  const labelS = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 5 };
  const ta = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '10px 12px', fontSize: 12, width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', resize: 'vertical' };
  const sel = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '5px 8px', fontSize: 12, width: '100%', boxSizing: 'border-box' };

  const cell = v => (v === '' || v == null) ? <span style={{ color: 'var(--ni-text-muted)' }}>—</span>
    : (v === true ? 'Yes' : v === false ? 'No' : String(v));

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
        onClick={() => fileRef.current && fileRef.current.click()}
        style={{ border: `2px dashed ${dragOver ? accent : 'var(--ni-border-soft)'}`, borderRadius: 10, padding: '18px', textAlign: 'center', cursor: 'pointer', marginBottom: 14, background: dragOver ? accent + '11' : 'transparent', transition: 'all 0.15s' }}>
        <div style={{ fontSize: 13, color: 'var(--ni-text)', fontWeight: 600 }}>
          Drop an Excel (.xlsx) or .csv file here, or click to choose
        </div>
        <div style={{ fontSize: 12, color: 'var(--ni-text-muted)', marginTop: 4 }}>
          {fileName ? `Loaded: ${fileName}` : 'The file is read locally in your browser — nothing is uploaded until you confirm'}
        </div>
        <input ref={fileRef} type="file"
          accept=".xlsx,.xlsm,.xls,.csv,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={e => onFile(e.target.files?.[0])} style={{ display: 'none' }} />
      </div>

      <div style={{ textAlign: 'center', color: 'var(--ni-text-muted)', fontSize: 11, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>— or paste from Excel —</div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelS}>Paste rows (tab-separated from Excel, or CSV)</label>
        <textarea value={raw} onChange={onPaste} rows={6}
          placeholder={(sampleHeaders || fields.slice(0, 4).map(f => f.label)).join('\t')}
          style={ta} />
      </div>

      {sheetNames.length > 1 && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...labelS, margin: 0 }}>Sheet</label>
          <select value={sheet} onChange={e => changeSheet(e.target.value)} style={{ ...sel, width: 'auto', minWidth: 200, maxWidth: '100%' }}>
            {sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: 11, color: 'var(--ni-text-muted)' }}>
            This workbook has {sheetNames.length} sheets — pick the one to import.
          </span>
        </div>
      )}

      {aoa.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...labelS, margin: 0 }}>Header row</label>
          <select value={headerRow} onChange={e => changeHeaderRow(Number(e.target.value))} style={{ ...sel, width: 'auto', minWidth: 260, maxWidth: '100%' }}>
            {aoa.slice(0, 30).map((r, i) => (
              <option key={i} value={i}>Row {i + 1}: {(r.filter(c => c !== '').slice(0, 4).join(' | ') || '(empty)').slice(0, 60)}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: 'var(--ni-text-muted)' }}>
            Auto-detected the column titles. Change this if a title/legend row was picked instead.
          </span>
        </div>
      )}

      {parsed.headers.length > 0 && (
        <>
          <div style={{ marginBottom: 6 }}><label style={labelS}>Map columns</label></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10, marginBottom: 8 }}>
            {parsed.headers.map((h, i) => (
              <div key={i} style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={h}>{h}</div>
                <select value={mapping[i] || ''} onChange={e => setColMap(i, e.target.value)} style={sel}>
                  {fieldOptions.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            ))}
          </div>
          {missingIds.length > 0 && (
            <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 12 }}>
              Map a column to <strong>{missingIds.map(f => f.label).join(' / ')}</strong> so each row can be identified.
            </div>
          )}

          <div style={{ marginBottom: 6 }}>
            <label style={labelS}>Preview — {previewRecords.length} row{previewRecords.length !== 1 ? 's' : ''}</label>
          </div>
          <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'auto', maxHeight: 320 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--ni-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(0,0,0,0.2)', position: 'sticky', top: 0 }}>
                  {fields.map(f => <th key={f.key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{f.label}</th>)}
                  {keepExtras && <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Extras</th>}
                </tr>
              </thead>
              <tbody>
                {previewRecords.length === 0 ? (
                  <tr><td colSpan={fields.length + (keepExtras ? 1 : 0)} style={{ padding: 24, textAlign: 'center', color: 'var(--ni-text-muted)' }}>
                    No rows yet — paste data or map a column.
                  </td></tr>
                ) : previewRecords.slice(0, 200).map((rec, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--ni-border-soft)' }}>
                    {fields.map(f => (
                      <td key={f.key} style={{ padding: '7px 12px', color: f.identifier ? accent : 'var(--ni-text-soft)', whiteSpace: 'nowrap', fontFamily: f.mono ? 'monospace' : 'inherit' }}>
                        {cell(rec[f.key])}
                      </td>
                    ))}
                    {keepExtras && (
                      <td style={{ padding: '7px 12px', color: 'var(--ni-text-muted)', fontSize: 11 }} title={(rec.attributes || []).map(a => `${a.key}: ${a.value}`).join(' · ')}>
                        {(rec.attributes || []).length ? `${rec.attributes.length} extra${rec.attributes.length !== 1 ? 's' : ''}` : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewRecords.length > 200 && (
            <div style={{ marginTop: 6, color: 'var(--ni-text-muted)', fontSize: 11 }}>
              Showing first 200 of {previewRecords.length} rows. All will be imported.
            </div>
          )}
        </>
      )}
    </div>
  );
}
